export function getTodayISO() {
  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(
    2,
    "0",
  );
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function formatDateForInput(value) {
  if (!value) return "";

  const stringValue = String(value);

  const match = stringValue.match(
    /^(\d{4})-(\d{2})-(\d{2})/,
  );

  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(
    2,
    "0",
  );
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function computeAnnualEndDate(startDate) {
  if (!startDate) return "";

  const match = String(startDate).match(
    /^(\d{4})-(\d{2})-(\d{2})$/,
  );

  if (!match) return "";

  const sourceYear = Number(match[1]);
  const sourceMonth = Number(match[2]);
  const sourceDay = Number(match[3]);

  const targetYear = sourceYear + 1;

  const lastDayOfTargetMonth = new Date(
    targetYear,
    sourceMonth,
    0,
  ).getDate();

  const targetDay = Math.min(
    sourceDay,
    lastDayOfTargetMonth,
  );

  return `${targetYear}-${String(sourceMonth).padStart(
    2,
    "0",
  )}-${String(targetDay).padStart(2, "0")}`;
}

export function normalizeItemType(subscription = {}) {
  if (subscription.item_type) {
    return subscription.item_type;
  }

  if (
    String(subscription.periodicity || "")
      .toLowerCase()
      .includes("ponct")
  ) {
    return "one_time";
  }

  return "subscription";
}

export function isUnsignedQuoteStatus(status = "") {
  const normalized = String(status)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  return (
    normalized.includes("devis") &&
    (
      normalized.includes("attente") ||
      normalized.includes("signer") ||
      normalized.includes("envoye")
    )
  );
}

export function isCancelledStatus(status = "") {
  const normalized = String(status)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  return (
    normalized.includes("annul") ||
    normalized.includes("resili") ||
    normalized.includes("refus")
  );
}

export function isSignedStatus(status = "") {
  if (!String(status || "").trim()) return false;
  if (isUnsignedQuoteStatus(status)) return false;
  if (isCancelledStatus(status)) return false;

  return true;
}

export function buildInitialSubscriptionForm({
  renewedFromSubscriptionId = null,
} = {}) {
  const today = getTodayISO();

  return {
    asso_id: "",
    item_type: "subscription",

    product: "",
    status: "Devis en attente de signature",

    start_date: today,
    end_date: computeAnnualEndDate(today),
    signed_at: "",

    periodicity: "annuel",

    total_amount: "",
    amount_paid: "0",
    paid_at: "",

    drive_invoice_link: "",
    invoice_date: "",
    payment_due_date: "",

    drive_quote_link: "",

    renewal_status: "not_started",
    renewal_quote_sent_at: "",
    last_reminder_at: "",

    renewed_from_subscription_id:
      renewedFromSubscriptionId || "",

    details: "",
  };
}

export function subscriptionToFormData(
  subscription = {},
) {
  const itemType = normalizeItemType(subscription);

  return {
    id: subscription.id,

    asso_id: subscription.asso_id ?? "",
    item_type: itemType,

    product: subscription.product || "",
    status:
      subscription.status ||
      "Devis en attente de signature",

    start_date: formatDateForInput(
      subscription.start_date,
    ),

    end_date:
      itemType === "one_time"
        ? ""
        : formatDateForInput(
            subscription.end_date,
          ),

    signed_at: formatDateForInput(
      subscription.signed_at,
    ),

    periodicity:
      itemType === "one_time"
        ? "ponctuel"
        : "annuel",

    total_amount:
      subscription.total_amount ?? "",

    amount_paid:
      subscription.amount_paid ?? "0",

    paid_at: formatDateForInput(
      subscription.paid_at,
    ),

    drive_invoice_link:
      subscription.drive_invoice_link || "",

    invoice_date: formatDateForInput(
      subscription.invoice_date,
    ),

    payment_due_date: formatDateForInput(
      subscription.payment_due_date,
    ),

    drive_quote_link:
      subscription.drive_quote_link || "",

    renewal_status:
      itemType === "one_time"
        ? "not_applicable"
        : subscription.renewal_status ||
          "not_started",

    renewal_quote_sent_at: formatDateForInput(
      subscription.renewal_quote_sent_at,
    ),

    last_reminder_at: formatDateForInput(
      subscription.last_reminder_at,
    ),

    renewed_from_subscription_id:
      subscription.renewed_from_subscription_id ||
      "",

    details: subscription.details || "",
  };
}

function isValidOptionalUrl(value) {
  if (!String(value || "").trim()) {
    return true;
  }

  try {
    const url = new URL(value);

    return ["http:", "https:"].includes(
      url.protocol,
    );
  } catch {
    return false;
  }
}

export function validateSubscriptionForm(
  formData,
) {
  const errors = {};

  const totalAmount = Number(
    formData.total_amount,
  );

  const amountPaid = Number(
    formData.amount_paid || 0,
  );

  if (!formData.asso_id) {
    errors.asso_id =
      "Sélectionne une association.";
  }

  if (!String(formData.product || "").trim()) {
    errors.product =
      "Renseigne un produit ou une prestation.";
  }

  if (!formData.status) {
    errors.status = "Sélectionne un statut.";
  }

  if (!formData.start_date) {
    errors.start_date =
      "La date de début est obligatoire.";
  }

  if (
    formData.item_type === "subscription" &&
    !formData.end_date
  ) {
    errors.end_date =
      "La date de fin est nécessaire pour suivre le renouvellement.";
  }

  if (
    formData.total_amount === "" ||
    Number.isNaN(totalAmount) ||
    totalAmount < 0
  ) {
    errors.total_amount =
      "Le montant total est invalide.";
  }

  if (
    Number.isNaN(amountPaid) ||
    amountPaid < 0
  ) {
    errors.amount_paid =
      "Le montant réglé est invalide.";
  }

  if (
    !Number.isNaN(totalAmount) &&
    !Number.isNaN(amountPaid) &&
    amountPaid > totalAmount
  ) {
    errors.amount_paid =
      "Le montant réglé ne peut pas dépasser le montant total.";
  }

  if (
    !isValidOptionalUrl(
      formData.drive_quote_link,
    )
  ) {
    errors.drive_quote_link =
      "Le lien du devis doit commencer par http:// ou https://.";
  }

  if (
    !isValidOptionalUrl(
      formData.drive_invoice_link,
    )
  ) {
    errors.drive_invoice_link =
      "Le lien de la facture doit commencer par http:// ou https://.";
  }

  return errors;
}

function cleanOptionalValue(value) {
  const cleaned = String(value || "").trim();

  return cleaned || null;
}

function cleanQuillContent(value) {
  const content = String(value || "");

  const textOnly = content
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

  return textOnly ? content : null;
}

export function buildSubscriptionPayload(
  formData,
) {
  const isOneTime =
    formData.item_type === "one_time";

  return {
    asso_id: Number(formData.asso_id),

    item_type: isOneTime
      ? "one_time"
      : "subscription",

    product: String(
      formData.product || "",
    ).trim(),

    status: formData.status,

    start_date: formData.start_date,

    end_date: isOneTime
      ? null
      : cleanOptionalValue(
          formData.end_date,
        ),

    signed_at: cleanOptionalValue(
      formData.signed_at,
    ),

    periodicity: isOneTime
      ? "ponctuel"
      : "annuel",

    total_amount: Number(
      formData.total_amount,
    ),

    amount_paid: Number(
      formData.amount_paid || 0,
    ),

    paid_at: cleanOptionalValue(
      formData.paid_at,
    ),

    drive_invoice_link: cleanOptionalValue(
      formData.drive_invoice_link,
    ),

    invoice_date: cleanOptionalValue(
      formData.invoice_date,
    ),

    payment_due_date: cleanOptionalValue(
      formData.payment_due_date,
    ),

    drive_quote_link: cleanOptionalValue(
      formData.drive_quote_link,
    ),

    renewal_status: isOneTime
      ? "not_applicable"
      : formData.renewal_status,

    renewal_quote_sent_at: isOneTime
      ? null
      : cleanOptionalValue(
          formData.renewal_quote_sent_at,
        ),

    last_reminder_at: isOneTime
      ? null
      : cleanOptionalValue(
          formData.last_reminder_at,
        ),

    renewed_from_subscription_id:
      formData.renewed_from_subscription_id
        ? Number(
            formData.renewed_from_subscription_id,
          )
        : null,

    details: cleanQuillContent(
      formData.details,
    ),
  };
}

export function getPaymentPreview(
  formData,
) {
  const totalAmount = Number(
    formData.total_amount || 0,
  );

  const amountPaid = Number(
    formData.amount_paid || 0,
  );

  const remainingAmount = Math.max(
    totalAmount - amountPaid,
    0,
  );

  const hasInvoice = Boolean(
    formData.invoice_date ||
    String(
      formData.drive_invoice_link || "",
    ).trim(),
  );

  const isPaid =
    totalAmount === 0 ||
    (
      totalAmount > 0 &&
      amountPaid >= totalAmount
    );

  let paymentStatus = "pending";

  if (!hasInvoice) {
    paymentStatus = "not_invoiced";
  } else if (isPaid) {
    paymentStatus = "paid";
  } else if (formData.payment_due_date) {
    const dueDate = new Date(
      `${formData.payment_due_date}T00:00:00`,
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    paymentStatus =
      dueDate < today
        ? "overdue"
        : amountPaid > 0
          ? "partial"
          : "pending";
  } else if (amountPaid > 0) {
    paymentStatus = "partial";
  }

  return {
    totalAmount,
    amountPaid,
    remainingAmount,
    paymentStatus,
    percentage:
      totalAmount > 0
        ? Math.min(
            Math.max(
              (amountPaid / totalAmount) * 100,
              0,
            ),
            100,
          )
        : 0,
  };
}